var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Download, Loader2, Square, Trash2 } from 'lucide-react';
import { useToast } from '@/hoc';
import { DEFAULT_RECORDING_CONFIG, expectedTotalBitrateBps, } from '@/context/recording-config';
import { SharedRecorder, buildLadder, selectProfile } from '@/recording/recorder';
import { ALL_PROFILE_IDS, RECORDING_PROFILES, getSupportedRecordingProfiles, isProfileId, } from '@/recording/profiles';
import { LocalBlobSink } from '@/recording/sinks';
import { THIRTY_MINUTES_SEC, measure } from '@/recording/metrics';
// Hooks come from the voice-admin client, never api-service — see the note on
// `useRecordingConfigAdmin` there for why the wrong one logs the operator out.
import { useRecordingConfigAdmin, useUpdateRecordingConfig } from './voice-admin-api';
import { AdminButton, Card, EmptyState, SectionHeader, Select } from './admin-ui';
/**
 * Record a sample with any config, watch it, keep it or bin it.
 *
 * Every recording-quality decision on this platform has so far been made by
 * arithmetic: bitrate x duration = megabytes. That arithmetic is right about
 * SIZE and says nothing about whether a reviewer can read the candidate's code,
 * which is the only question that actually matters. This exists so the choice
 * comes from footage instead.
 *
 * It runs the SAME recorder a real interview runs — `SharedRecorder` — and only
 * swaps the destination: `LocalBlobSink` instead of the upload path. Nothing
 * here touches S3, the backend upload APIs, or the database. Samples live in
 * this tab's memory as blob URLs and are gone on navigate or refresh, which is
 * why Download is offered prominently.
 *
 * ON THE NUMBERS. "Requested" is what we asked the encoder for. "Measured" is
 * bytes-over-seconds on the file that came out. They are shown separately and
 * never conflated, because MediaRecorder treats a bitrate as a hint: a real run
 * asked for 500 kbps and produced 104.
 */
const WIDTHS = [640, 960, 1280, 1600, 1920];
const FRAME_RATES = [4, 5, 8, 12, 15, 24, 30];
const VIDEO_BITRATES = [300, 500, 800, 1000, 1500, 2000, 3000, 5000]; // kbps
const AUDIO_BITRATES = [24, 32, 64, 96, 128]; // kbps
const fmtMB = (bytes) => `${(bytes / 1048576).toFixed(2)} MB`;
const fmtKbps = (bps) => `${Math.round(bps / 1000)} kbps`;
const RecordingBenchPanel = ({ preset } = {}) => {
    var _a;
    const { showToast } = useToast();
    const { data: liveCfg, isLoading: liveCfgLoading } = useRecordingConfigAdmin();
    const applyMut = useUpdateRecordingConfig();
    const [source, setSource] = useState('screen');
    const [width, setWidth] = useState(1280);
    const [frameRate, setFrameRate] = useState(8);
    const [videoKbps, setVideoKbps] = useState(500);
    const [audioKbps, setAudioKbps] = useState(32);
    const [withAudio, setWithAudio] = useState(true);
    // null means "whatever the live config says". The bench is for testing the
    // configuration you just saved, so it has to FOLLOW that by default —
    // reporting a profile nobody chose is exactly the misleading result the
    // capability box exists to prevent. Choosing here overrides locally, like
    // every other control on this panel, without saving anything.
    const [profileOverride, setProfileOverride] = useState(null);
    /**
     * Open on the configuration the operator just came from.
     *
     * Retyping four settings to test the thing you were already looking at is
     * where a bench stops being used — and worse, where a run silently tests
     * something other than what was configured. `preset` carries the values from
     * the Recording Quality panel (including edits not yet saved); the saved
     * config is the fallback when the bench is opened directly.
     *
     * Seeded ONCE. After that the controls belong to whoever is driving them, so
     * a late-arriving fetch cannot yank a value out from under an experiment.
     */
    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current)
            return;
        const src = preset !== null && preset !== void 0 ? preset : liveCfg;
        if (!src)
            return;
        seededRef.current = true;
        if (typeof src.max_width === 'number')
            setWidth(src.max_width);
        if (typeof src.max_frame_rate === 'number')
            setFrameRate(src.max_frame_rate);
        if (typeof src.video_bitrate_bps === 'number') {
            setVideoKbps(Math.round(src.video_bitrate_bps / 1000));
        }
        if (typeof src.audio_bitrate_bps === 'number') {
            setAudioKbps(Math.round(src.audio_bitrate_bps / 1000));
        }
        if (isProfileId(src.encoding_profile))
            setProfileOverride(src.encoding_profile);
    }, [preset, liveCfg]);
    const [recording, setRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [liveBytes, setLiveBytes] = useState(0);
    const [error, setError] = useState(null);
    const [samples, setSamples] = useState([]);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);
    const sinkRef = useRef(null);
    const tickRef = useRef(null);
    const previewRef = useRef(null);
    const nextIdRef = useRef(1);
    // Every URL handed out, so unmount can revoke them. Without this each sample
    // leaks its blob for the lifetime of the tab, and these are tens of megabytes.
    const urlsRef = useRef([]);
    const liveProfileId = isProfileId(preset === null || preset === void 0 ? void 0 : preset.encoding_profile)
        ? preset.encoding_profile
        : isProfileId(liveCfg === null || liveCfg === void 0 ? void 0 : liveCfg.encoding_profile)
            ? liveCfg.encoding_profile
            : DEFAULT_RECORDING_CONFIG.encoding_profile;
    const effectiveProfileId = profileOverride !== null && profileOverride !== void 0 ? profileOverride : liveProfileId;
    /** The config the bench is currently exercising. */
    const benchConfig = useMemo(() => (Object.assign(Object.assign({}, DEFAULT_RECORDING_CONFIG), { encoding_profile: effectiveProfileId, max_width: width, max_frame_rate: frameRate, video_bitrate_bps: videoKbps * 1000, audio_bitrate_bps: audioKbps * 1000, audio_enabled: withAudio })), [effectiveProfileId, width, frameRate, videoKbps, audioKbps, withAudio]);
    /**
     * What THIS browser can encode, from the shared capability resolver — the
     * same one an interview uses, so the bench cannot report a different answer
     * than the thing it is meant to predict.
     *
     * The bench runs on the multipart transport because that is the only one that
     * can carry a non-WebM container; a legacy interview would see the WebM
     * subset of this list.
     */
    const profileSupport = useMemo(() => getSupportedRecordingProfiles(benchConfig.audio_enabled), [benchConfig.audio_enabled]);
    /** Requested vs supported vs selected — the three an admin has to tell apart
     *  to know whether they are testing their choice or a fallback. */
    const capability = useMemo(() => {
        var _a;
        const requestedId = benchConfig.encoding_profile;
        const requested = requestedId === 'auto' ? null : RECORDING_PROFILES[requestedId];
        const requestedSupport = (_a = profileSupport.find((p) => p.id === requestedId)) !== null && _a !== void 0 ? _a : null;
        const { profile, reason } = selectProfile(benchConfig, undefined, 's3-multipart');
        return {
            requestedLabel: requested ? requested.label : 'Auto',
            requestedSupported: requestedId === 'auto' ? null : Boolean(requestedSupport === null || requestedSupport === void 0 ? void 0 : requestedSupport.supported),
            selected: profile,
            selectedLabel: profile
                ? profile.profileId
                    ? RECORDING_PROFILES[profile.profileId].label
                    : profile.mimeType
                : null,
            isFallback: Boolean(profile && requestedId !== 'auto' && profile.profileId !== requestedId),
            blockedReason: profile ? null : reason !== null && reason !== void 0 ? reason : 'no supported recording profile',
        };
    }, [benchConfig, profileSupport]);
    const ladderSupport = useMemo(() => {
        const rungs = buildLadder(benchConfig, 's3-multipart');
        const byMime = new Map(profileSupport.flatMap((p) => p.attempted.map((a) => [a.mimeType, a.supported])));
        return rungs.map((r) => {
            var _a;
            return (Object.assign(Object.assign({}, r), { supported: (_a = byMime.get(r.mimeType)) !== null && _a !== void 0 ? _a : false }));
        });
    }, [benchConfig, profileSupport]);
    const stopTracks = useCallback(() => {
        var _a;
        (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getTracks().forEach((t) => {
            try {
                t.stop();
            }
            catch (_a) {
                /* already stopped */
            }
        });
        streamRef.current = null;
        if (previewRef.current)
            previewRef.current.srcObject = null;
    }, []);
    const stop = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        if (tickRef.current) {
            window.clearInterval(tickRef.current);
            tickRef.current = null;
        }
        const rec = recorderRef.current;
        const sink = sinkRef.current;
        setRecording(false);
        if (!rec || !sink)
            return;
        const track = (_a = streamRef.current) === null || _a === void 0 ? void 0 : _a.getVideoTracks()[0];
        const settings = (_c = (_b = track === null || track === void 0 ? void 0 : track.getSettings) === null || _b === void 0 ? void 0 : _b.call(track)) !== null && _c !== void 0 ? _c : {};
        // Wall clock, NOT HTMLVideoElement.duration: a concatenated MediaRecorder
        // WebM reports Infinity, so the element cannot tell us how long this is.
        const durationSec = rec.elapsedSec;
        yield rec.stop();
        const blob = sink.toBlob();
        const url = sink.toObjectUrl();
        urlsRef.current.push(url);
        const profile = rec.profileInUse;
        setSamples((prev) => {
            var _a, _b, _c;
            return [
                {
                    id: nextIdRef.current++,
                    url,
                    blob,
                    mime: profile.mimeType,
                    filename: `bench_${(_a = settings.width) !== null && _a !== void 0 ? _a : width}x${(_b = settings.height) !== null && _b !== void 0 ? _b : '?'}_${frameRate}fps_${videoKbps}kbps.${(_c = profile.extension) !== null && _c !== void 0 ? _c : 'webm'}`,
                    measurement: measure(blob.size, durationSec, expectedTotalBitrateBps(benchConfig), {
                        width: settings.width,
                        height: settings.height,
                        frameRate: settings.frameRate,
                    }),
                    requested: { width, frameRate, videoKbps, audioKbps },
                },
                ...prev,
            ];
        });
        recorderRef.current = null;
        sinkRef.current = null;
        stopTracks();
    }), [benchConfig, frameRate, videoKbps, audioKbps, width, stopTracks]);
    const start = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        setError(null);
        // Pick the profile exactly as an interview would, so a rung that falls back
        // here is the rung a candidate would get.
        const { profile, reason } = selectProfile(benchConfig, undefined, 's3-multipart');
        if (!profile) {
            setError(reason !== null && reason !== void 0 ? reason : 'no supported recording profile');
            return;
        }
        try {
            // The same constraint SHAPE production uses: a ceiling, not a request. The
            // browser may hand back less, and that gap between requested and actual is
            // exactly what the results show.
            const videoConstraints = {
                width: { max: profile.maxWidth },
                frameRate: { max: profile.maxFrameRate },
            };
            let stream;
            if (source === 'screen') {
                stream = yield navigator.mediaDevices.getDisplayMedia({ video: videoConstraints, audio: false });
            }
            else {
                stream = yield navigator.mediaDevices.getUserMedia({
                    video: videoConstraints,
                    audio: false,
                });
            }
            if (withAudio) {
                // Echo cancellation on, as production does — it changes what the encoder
                // sees, so leaving it off would not be a like-for-like test.
                const micStream = yield navigator.mediaDevices.getUserMedia({
                    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
                });
                micStream.getAudioTracks().forEach((t) => stream.addTrack(t));
            }
            streamRef.current = stream;
            if (previewRef.current) {
                previewRef.current.srcObject = stream;
                // Older specs (and jsdom) return undefined rather than a Promise, so
                // calling .catch() unguarded is itself a TypeError that would abort the
                // whole start path.
                void ((_a = previewRef.current.play()) === null || _a === void 0 ? void 0 : _a.catch(() => undefined));
            }
            const sink = new LocalBlobSink(profile.mimeType);
            const rec = new SharedRecorder(sink, profile, {
                onFailure: (why) => setError(why),
            });
            if (!rec.start(stream)) {
                stopTracks();
                return;
            }
            sinkRef.current = sink;
            recorderRef.current = rec;
            setElapsed(0);
            setLiveBytes(0);
            setRecording(true);
            tickRef.current = window.setInterval(() => {
                setElapsed(Math.round(rec.elapsedSec));
                setLiveBytes(sink.bytesAccepted);
            }, 500);
            // If the share is stopped from the browser's own bar, stop with it.
            (_b = stream.getVideoTracks()[0]) === null || _b === void 0 ? void 0 : _b.addEventListener('ended', () => void stop());
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            stopTracks();
        }
    }), [benchConfig, source, withAudio, stopTracks, stop]);
    const discard = useCallback((id) => {
        setSamples((prev) => {
            const target = prev.find((s) => s.id === id);
            if (target) {
                try {
                    URL.revokeObjectURL(target.url);
                }
                catch (_a) {
                    /* already revoked */
                }
                urlsRef.current = urlsRef.current.filter((u) => u !== target.url);
            }
            return prev.filter((s) => s.id !== id);
        });
    }, []);
    const download = useCallback((sample) => {
        // The Safari-safe pattern used elsewhere in the console: append the anchor,
        // click, remove, and revoke on the NEXT tick — revoking synchronously can
        // race the download in Safari.
        if (typeof document === 'undefined' || typeof (URL === null || URL === void 0 ? void 0 : URL.createObjectURL) !== 'function')
            return;
        const url = URL.createObjectURL(sample.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = sample.filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }, []);
    const applyToLive = useCallback((sample) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield applyMut.mutateAsync({
                max_width: sample.requested.width,
                max_frame_rate: sample.requested.frameRate,
                video_bitrate_bps: sample.requested.videoKbps * 1000,
                audio_bitrate_bps: sample.requested.audioKbps * 1000,
            });
            showToast({ message: 'Applied to live recording settings', status: 'success' });
        }
        catch (e) {
            showToast({
                message: e instanceof Error ? e.message : 'Could not apply settings',
                status: 'error',
            });
        }
    }), [applyMut, showToast]);
    // Unmount: stop anything running, release tracks, revoke every URL handed out.
    useEffect(() => () => {
        var _a, _b;
        if (tickRef.current)
            window.clearInterval(tickRef.current);
        try {
            void ((_a = recorderRef.current) === null || _a === void 0 ? void 0 : _a.stop());
        }
        catch (_c) {
            /* nothing recording */
        }
        (_b = streamRef.current) === null || _b === void 0 ? void 0 : _b.getTracks().forEach((t) => {
            try {
                t.stop();
            }
            catch (_a) {
                /* already stopped */
            }
        });
        urlsRef.current.forEach((u) => {
            try {
                URL.revokeObjectURL(u);
            }
            catch (_a) {
                /* already revoked */
            }
        });
        urlsRef.current = [];
    }, []);
    // The profile label, not the bare codec: the container is half the answer
    // and an admin comparing this against their configuration needs both.
    const liveProfileLabel = (() => {
        var _a, _b;
        const id = liveCfg === null || liveCfg === void 0 ? void 0 : liveCfg.encoding_profile;
        if (!id || id === 'auto')
            return id === 'auto' ? 'Auto' : '—';
        return (_b = (_a = RECORDING_PROFILES[id]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : String(id);
    })();
    const liveSummary = liveCfg
        ? `${liveProfileLabel} · ${liveCfg.max_width}px · ${liveCfg.max_frame_rate} fps · ${Math.round(liveCfg.video_bitrate_bps / 1000)} kbps`
        : '—';
    return (_jsxs(Card, { children: [_jsx(SectionHeader, { title: 'Recording Bench', subtitle: 'Record a sample with a candidate configuration, watch it, and keep or discard it. Nothing here is uploaded.' }), _jsxs("p", { className: 'mb-4 text-sm text-dash-muted', children: ["Live interview settings:", ' ', liveCfgLoading ? (_jsxs("span", { role: 'status', "aria-busy": 'true', className: 'inline-block align-middle', children: [_jsx("span", { className: 'sr-only', children: "Loading live settings" }), _jsx("span", { "aria-hidden": 'true', className: 'dash-skeleton block h-3.5 w-56 rounded' })] })) : (liveSummary)] }), _jsxs("div", { className: 'mb-4 rounded-lg border border-dash-border bg-dash-surface p-3 text-sm', "data-testid": 'bench-capability', children: [_jsxs("div", { className: 'flex flex-wrap items-center gap-x-6 gap-y-1', children: [_jsxs("span", { className: 'text-dash-muted', children: ["Requested: ", _jsx("span", { className: 'text-dash-text', children: capability.requestedLabel })] }), _jsxs("span", { className: 'text-dash-muted', children: ["Browser support:", ' ', capability.requestedSupported === null ? (_jsx("span", { className: 'text-dash-text', children: "n/a (auto)" })) : capability.requestedSupported ? (_jsx("span", { className: 'font-medium text-emerald-500', children: "YES" })) : (_jsx("span", { className: 'font-medium text-amber-500', children: "NO" }))] }), _jsxs("span", { className: 'text-dash-muted', children: ["Selected:", ' ', _jsx("span", { className: 'text-dash-text', children: (_a = capability.selectedLabel) !== null && _a !== void 0 ? _a : 'NOT SUPPORTED' })] }), capability.isFallback && (_jsx("span", { className: 'font-medium text-amber-500', children: "Fallback: YES \u2014 this is not the profile you asked for" }))] }), capability.blockedReason && (_jsx("p", { className: 'mt-2 text-amber-500', children: capability.blockedReason })), _jsx("ul", { className: 'mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-dash-muted', children: profileSupport.map((p) => (_jsxs("li", { children: [p.label, ": ", p.supported ? '✓' : '✗'] }, p.id))) })] }), _jsxs("div", { className: 'flex flex-col gap-4 aiLg:flex-row', children: [_jsxs("aside", { className: 'flex w-full shrink-0 flex-col gap-3 aiLg:w-[320px]', children: [_jsx(Select, { label: 'Source', value: source, onChange: (v) => setSource(v), options: [
                                    { value: 'screen', label: 'Screen share (as an interview)' },
                                    { value: 'camera', label: 'Camera' },
                                ] }), _jsx(Select, { label: 'Encoding', value: effectiveProfileId, onChange: (v) => setProfileOverride(v), options: [
                                    { value: 'auto', label: 'Auto (browser picks)' },
                                    ...ALL_PROFILE_IDS.map((id) => ({
                                        value: id,
                                        label: RECORDING_PROFILES[id].label,
                                    })),
                                ] }), _jsx(Select, { label: 'Max width', value: String(width), onChange: (v) => setWidth(Number(v)), options: WIDTHS.map((w) => ({ value: String(w), label: `${w}px` })) }), _jsx(Select, { label: 'Max frame rate', value: String(frameRate), onChange: (v) => setFrameRate(Number(v)), options: FRAME_RATES.map((f) => ({ value: String(f), label: `${f} fps` })) }), _jsx(Select, { label: 'Requested video bitrate', value: String(videoKbps), onChange: (v) => setVideoKbps(Number(v)), options: VIDEO_BITRATES.map((b) => ({ value: String(b), label: `${b} kbps` })) }), _jsx(Select, { label: 'Requested audio bitrate', value: String(audioKbps), onChange: (v) => setAudioKbps(Number(v)), options: AUDIO_BITRATES.map((b) => ({ value: String(b), label: `${b} kbps` })) }), _jsxs("label", { className: 'flex items-center gap-2 text-sm text-dash-text', children: [_jsx("input", { type: 'checkbox', checked: withAudio, onChange: (e) => setWithAudio(e.target.checked) }), "Record audio"] }), _jsxs("div", { className: 'rounded-lg border border-dash-border p-3 text-xs text-dash-muted', children: [_jsx("div", { className: 'mb-1 font-medium text-dash-text', children: "Codec support (probed)" }), ladderSupport.map((r) => (_jsxs("div", { "data-testid": 'ladder-rung', children: [r.supported ? '✓' : '✕', " rung ", r.rung, ": ", r.label] }, `${r.mimeType}-${r.rung}`)))] }), !recording ? (_jsxs(AdminButton, { onClick: () => void start(), "aria-label": 'Start test recording', children: [_jsx(Circle, { size: 14 }), " Start test recording"] })) : (_jsxs(AdminButton, { onClick: () => void stop(), "aria-label": 'Stop test recording', children: [_jsx(Square, { size: 14 }), " Stop (", elapsed, "s \u00B7 ", fmtMB(liveBytes), ")"] })), error && (_jsx("p", { className: 'text-sm text-red-500', role: 'alert', children: error }))] }), _jsxs("div", { className: 'min-w-0 flex-1', children: [_jsx("video", { ref: previewRef, muted: true, playsInline: true, className: 'mb-4 block max-h-[280px] w-full rounded-lg bg-black object-contain' }), samples.length === 0 ? (_jsx(EmptyState, { title: 'No samples yet', hint: 'Record one to compare.' })) : (_jsx("ul", { className: 'flex flex-col gap-4', children: samples.map((s) => {
                                    var _a, _b, _c;
                                    const m = s.measurement;
                                    return (_jsxs("li", { "data-testid": 'bench-sample', className: 'rounded-lg border border-dash-border p-3', children: [_jsx("video", { src: s.url, controls: true, className: 'mb-3 w-full rounded bg-black' }), _jsxs("dl", { className: 'grid grid-cols-2 gap-x-4 gap-y-1 text-xs', children: [_jsx("dt", { className: 'text-dash-muted', children: "Duration" }), _jsxs("dd", { children: [m.durationSec.toFixed(1), "s"] }), _jsx("dt", { className: 'text-dash-muted', children: "File size" }), _jsx("dd", { children: fmtMB(m.sizeBytes) }), _jsx("dt", { className: 'text-dash-muted', children: "Requested resolution" }), _jsxs("dd", { children: [s.requested.width, "px (max)"] }), _jsx("dt", { className: 'text-dash-muted', children: "Actual track resolution" }), _jsxs("dd", { children: [(_a = m.actual.width) !== null && _a !== void 0 ? _a : '?', "\u00D7", (_b = m.actual.height) !== null && _b !== void 0 ? _b : '?'] }), _jsx("dt", { className: 'text-dash-muted', children: "Requested FPS" }), _jsx("dd", { children: s.requested.frameRate }), _jsx("dt", { className: 'text-dash-muted', children: "Actual track FPS" }), _jsx("dd", { children: (_c = m.actual.frameRate) !== null && _c !== void 0 ? _c : '?' }), _jsx("dt", { className: 'text-dash-muted', children: "MIME / container" }), _jsx("dd", { className: 'truncate', children: s.mime }), _jsx("dt", { className: 'text-dash-muted', children: "Requested total bitrate" }), _jsx("dd", { children: fmtKbps(m.expectedBitrateBps) }), _jsx("dt", { className: 'text-dash-muted', children: "Measured total bitrate" }), _jsxs("dd", { "data-testid": 'measured-bitrate', children: [fmtKbps(m.measuredBitrateBps), ' ', _jsxs("span", { className: m.band === 'pass'
                                                                    ? 'text-green-500'
                                                                    : m.band === 'warn'
                                                                        ? 'text-amber-500'
                                                                        : 'text-red-500', children: ["(", m.band, ")"] })] }), _jsx("dt", { className: 'text-dash-muted', children: "Estimated 30-minute size" }), _jsxs("dd", { "data-testid": 'thirty-min-estimate', children: ["~", fmtMB(m.thirtyMinuteEstimateBytes), ' ', _jsx("span", { className: 'text-dash-muted', children: "(estimate)" })] })] }), _jsxs("p", { className: 'mt-2 text-[11px] text-dash-muted', children: ["Requested bitrates are what the encoder was asked for; only the total is measurable, and the browser may not honour the request. The 30-minute figure extrapolates the measured rate over ", THIRTY_MINUTES_SEC / 60, " minutes and is an estimate \u2014 screen content dominates."] }), _jsxs("div", { className: 'mt-3 flex flex-wrap gap-2', children: [_jsxs(AdminButton, { onClick: () => download(s), "aria-label": 'Download sample', children: [_jsx(Download, { size: 14 }), " Download"] }), _jsxs(AdminButton, { onClick: () => discard(s.id), "aria-label": 'Discard sample', children: [_jsx(Trash2, { size: 14 }), " Discard"] }), _jsxs(AdminButton, { onClick: () => void applyToLive(s), disabled: applyMut.isPending, "aria-label": 'Apply to live', children: [applyMut.isPending ? _jsx(Loader2, { size: 14, className: 'animate-spin' }) : null, "Apply to live"] })] })] }, s.id));
                                }) }))] })] })] }));
};
export default RecordingBenchPanel;
